import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const evidenceItems = [
  {
    id: 'policy',
    code: 'EV-01',
    type: 'POLICY_ARTIFACT',
    statusBadge: 'DESIGN_ONLY',
    label: 'Written backup policy',
    shortLabel: 'Policy document',
    supports:
      'Establishes management intent and defines the required standard: daily incremental, weekly full, 30-day retention. Confirms a standard exists to evaluate against.',
    unproven:
      'Whether the policy is known to operations, implemented technically, or enforced. A policy on a drive is not an operating control.',
    nextQuestion:
      'Who is responsible for executing this schedule? Can you show me the backup job configuration in the console that implements these retention rules?',
    inherentRisk: 'High',
    designRating: 'Effective',
    operatingRating: 'Unverified',
    evidenceSample: {
      type: 'document',
      label: 'Policy extract (IT-SEC-POL-04.2)',
      content:
        'Section 4.2: Backup jobs shall execute daily for production databases. Full backups shall be retained for 30 days minimum. Restoration tests shall be performed quarterly.',
      highlight: 'Restoration tests shall be performed quarterly.',
      stamp: 'CLASSIFIED // INTERNAL GOVERNANCE',
    },
  },
  {
    id: 'console',
    code: 'EV-02',
    type: 'SYS_CONSOLE_LOG',
    statusBadge: 'EXCEPTION_FLAGGED',
    label: 'Job log showing success',
    shortLabel: 'Console log',
    supports:
      'Confirms that on the sampled dates, jobs ran and returned a status of success. Indicates the automation is operating.',
    unproven:
      'Whether the data written is recoverable, whether all critical databases are included in the job scope, or whether exceptions are alerted to anyone.',
    nextQuestion:
      'When did a job last fail, and what did the alert look like? When was the last restore test from these backups performed and documented?',
    inherentRisk: 'High',
    designRating: 'Effective',
    operatingRating: 'Deficient (Unaddressed Exception)',
    evidenceSample: {
      type: 'table',
      label: 'SysAdmin Backup Console Export (Production Tier-1)',
      rows: [
        { date: '2024-08-12 02:00:04', job: 'DB_PROD_DAILY', status: 'Success', size: '142 GB' },
        { date: '2024-08-11 02:00:02', job: 'DB_PROD_DAILY', status: 'Success', size: '141 GB' },
        { date: '2024-08-10 02:00:03', job: 'DB_PROD_DAILY', status: 'Failed', size: '0 GB' },
        { date: '2024-08-09 02:00:01', job: 'DB_PROD_DAILY', status: 'Success', size: '140 GB' },
      ],
      stamp: 'SYS_TELEMETRY_LOG',
    },
  },
  {
    id: 'restore',
    code: 'EV-03',
    type: 'TEST_RECORD',
    statusBadge: 'PARTIAL_PASS',
    label: 'Restore test result',
    shortLabel: 'Test result',
    supports:
      'Confirms that a restore was attempted and the data was verified as usable. Within the scope of what was tested, recovery worked.',
    unproven:
      'Whether the test covers all critical systems, all data types, or all recovery scenarios. A single test result reflects one test, not general recovery capability.',
    nextQuestion:
      'What was the scope of this test? Which systems were included, and what was the recovery time target versus actual? Is this tested regularly?',
    inherentRisk: 'Medium',
    designRating: 'Effective',
    operatingRating: 'Partially Operating (Scope Limited)',
    evidenceSample: {
      type: 'checklist',
      label: 'Disaster Recovery Restoration Test Record (DR-TR-24)',
      checks: [
        { item: 'Target: System A Database Cluster', status: 'PASSED' },
        { item: 'Data Checksum Verification', status: 'MATCHED' },
        { item: 'Recovery Time Actual: 3h 22m (Target: 4h 00m)', status: 'WITHIN_SLA' },
        { item: 'Secondary Systems (Systems B & C)', status: 'OUT_OF_SCOPE' },
      ],
      stamp: 'OPERATIONAL VERIFICATION',
    },
  },
  {
    id: 'followup',
    code: 'EV-04',
    type: 'INCIDENT_RECORD',
    statusBadge: 'CORRECTED',
    label: 'Follow-up record',
    shortLabel: 'Follow-up',
    supports:
      'Shows that the Day 3 failure was identified, addressed, and a corrective action was taken. Demonstrates that exceptions are tracked and not ignored.',
    unproven:
      'Whether the root cause of the failure was fully resolved. A corrective action being logged does not confirm the fix was effective long-term or that similar failures won’t recur.',
    nextQuestion:
      'What was the root cause of the Day 3 failure? Has the fix been verified through subsequent successful runs? Is there a threshold for how many failures trigger escalation?',
    inherentRisk: 'Medium',
    designRating: 'Effective',
    operatingRating: 'Remediated (Monitoring Ongoing)',
    evidenceSample: {
      type: 'document',
      label: 'ITSM Incident Post-Mortem Ticket (INC-889412)',
      content:
        'Backup failure logged: Day 3, System A. Root cause: Storage path unavailable due to maintenance window overlap. Corrective action: Maintenance schedule updated to exclude backup window. Verified: Day 4 backup completed successfully.',
      highlight: 'Maintenance schedule updated to exclude backup window.',
      stamp: 'CLOSED_WITH_ACTIONS',
    },
  },
];

const panelVariants = {
  enter: { opacity: 0, y: 8, clipPath: 'inset(0 0 4% 0)' },
  center: { opacity: 1, y: 0, clipPath: 'inset(0 0 0% 0)' },
  exit: { opacity: 0, y: -8, clipPath: 'inset(0 0 4% 0)' },
};

function EvidenceArtifactView({ sample }) {
  if (!sample) return null;

  if (sample.type === 'table') {
    return (
      <div className="space-y-3 font-mono">
        <div className="flex justify-between items-center text-[9px] uppercase tracking-wider text-stone-500 border-b border-stone-200 pb-2">
          <span>{sample.label}</span>
          <span className="text-yellow-600">{sample.stamp}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="text-[11px] text-stone-700 w-full border-collapse">
            <thead>
              <tr className="border-b border-stone-200 text-stone-500 text-[9px] uppercase tracking-widest text-left">
                <th className="py-2 pr-3">Timestamp</th>
                <th className="py-2 pr-3">Job ID</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Payload</th>
              </tr>
            </thead>
            <tbody>
              {sample.rows.map((row, i) => (
                <tr
                  key={i}
                  className={`border-b border-stone-100 ${
                    row.status === 'Failed' ? 'bg-yellow-50/70 text-stone-900 font-semibold' : ''
                  }`}
                >
                  <td className="py-2 pr-3 text-stone-500">{row.date}</td>
                  <td className="py-2 pr-3">{row.job}</td>
                  <td className="py-2 pr-3">
                    <span
                      className={`px-1.5 py-0.5 rounded-xs text-[9px] uppercase ${
                        row.status === 'Failed'
                          ? 'bg-yellow-500 text-stone-900 font-bold'
                          : 'text-stone-600 bg-stone-100'
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="py-2 text-stone-500">{row.size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (sample.type === 'checklist') {
    return (
      <div className="space-y-3 font-mono">
        <div className="flex justify-between items-center text-[9px] uppercase tracking-wider text-stone-500 border-b border-stone-200 pb-2">
          <span>{sample.label}</span>
          <span className="text-stone-700">{sample.stamp}</span>
        </div>
        <div className="space-y-2">
          {sample.checks.map((chk, i) => (
            <div
              key={i}
              className="flex items-center justify-between p-2.5 bg-white border border-stone-200 rounded-xs text-[11px]"
            >
              <span className="text-stone-700">{chk.item}</span>
              <span
                className={`text-[9px] px-2 py-0.5 font-bold rounded-xs ${
                  chk.status === 'OUT_OF_SCOPE'
                    ? 'bg-stone-100 text-stone-500 border border-stone-300'
                    : 'bg-stone-900 text-white'
                }`}
              >
                {chk.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 font-mono">
      <div className="flex justify-between items-center text-[9px] uppercase tracking-wider text-stone-500 border-b border-stone-200 pb-2">
        <span>{sample.label}</span>
        <span className="text-yellow-700 font-medium">{sample.stamp}</span>
      </div>
      <div className="bg-stone-50/80 border border-stone-200/90 p-4 text-[12px] text-stone-700 leading-relaxed relative">
        <p>{sample.content}</p>
        {sample.highlight && (
          <div className="mt-3 pt-2 border-t border-stone-200 text-[11px] text-stone-600">
            <span className="bg-yellow-200/80 px-1 py-0.5 text-stone-900 font-semibold">
              KEY AUDIT ANCHOR:
            </span>{' '}
            {sample.highlight}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AssessmentSim() {
  const [selected, setSelected] = useState('policy');
  const [workbenchTab, setWorkbenchTab] = useState('findings');

  const active = evidenceItems.find((e) => e.id === selected) || evidenceItems[0];

  return (
    <section id="section-assess" className="snap-start min-h-screen bg-stone-100/60 w-full px-4 sm:px-5 py-10 sm:py-12 md:p-20 relative">
      <div className="max-w-6xl mx-auto pt-4">
        {/* Section header */}
        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 border-b border-stone-900 pb-4 mb-6 md:mb-8">
          <div>
            <h2 className="text-xl font-serif italic text-stone-900">How I assess</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-700 bg-white border border-stone-300 px-2 py-1">
              CONTROL: ISO 27001 A.12.3.1 (BACKUP)
            </span>
          </div>
        </div>

        <p className="text-[12.5px] sm:text-[13px] text-stone-600 leading-relaxed mb-6 sm:mb-8 max-w-2xl">
          An effective audit separates stated policy from verifiable operating controls. Select an evidence artifact below to test how an assessor interrogates evidence: what it proves, what it leaves unproven, and the next substantive inquiry.
        </p>

        {/* Dual Pane Workbench */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">
          {/* Left Pane: Evidence Locker */}
          <div className="md:col-span-5 lg:col-span-4 space-y-2">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500">
                EVIDENCE LOCKER
              </span>
              <span className="font-mono text-[9px] text-stone-400">
                4 SAMPLES LOADED
              </span>
            </div>

            <div className="space-y-2">
              {evidenceItems.map((item) => {
                const isSelected = selected === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setSelected(item.id)}
                    aria-pressed={isSelected}
                    className={`relative w-full text-left p-3 sm:p-3.5 border transition-all duration-200 flex flex-col justify-between gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 ${
                      isSelected
                        ? 'bg-stone-900 text-white border-stone-900 shadow-sm ring-1 ring-stone-900/10'
                        : 'bg-white text-stone-800 border-stone-200/90 hover:border-stone-500 hover:shadow-2xs'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className={`font-mono text-[9px] tracking-wider ${isSelected ? 'text-yellow-400' : 'text-stone-500'}`}>
                        [{item.code}]
                      </span>
                      <span className={`font-mono text-[8.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-xs border ${
                        isSelected
                          ? 'border-yellow-500/40 text-yellow-300 bg-yellow-500/10'
                          : 'border-stone-200 text-stone-500 bg-stone-50'
                      }`}>
                        {item.statusBadge}
                      </span>
                    </div>

                    <span className="text-xs font-semibold leading-snug">
                      {item.label}
                    </span>

                    {/* Left active marker */}
                    {isSelected && (
                      <motion.div
                        layoutId="active-evidence-pill"
                        className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-500"
                        transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Pane: Assessor Working Paper & Terminal */}
          <div className="md:col-span-7 lg:col-span-8">
            <div className="bg-white border border-stone-900 shadow-md">
              {/* Working Paper Metadata Bar */}
              <div className="bg-stone-900 text-stone-300 px-4 sm:px-6 py-3 border-b border-stone-800 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white font-medium">
                    WORKING PAPER // {active.code} : {active.shortLabel}
                  </span>
                </div>
                <span className="font-mono text-[9px] text-stone-400 uppercase tracking-widest">
                  STATUS: EVALUATING
                </span>
              </div>

              {/* Workbench Tabs */}
              <div className="flex border-b border-stone-200 px-4 sm:px-6 bg-stone-50/60 overflow-x-auto">
                {[
                  { id: 'findings', label: '01 / Assessor Evaluation' },
                  { id: 'artifact', label: '02 / Raw Evidence Sample' },
                  { id: 'gauge', label: '03 / Control Strength Gauge' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setWorkbenchTab(tab.id)}
                    className={`relative py-2.5 px-3 text-[10px] font-mono uppercase tracking-wider whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-stone-900 ${
                      workbenchTab === tab.id
                        ? 'text-stone-900 font-bold'
                        : 'text-stone-500 hover:text-stone-800'
                    }`}
                  >
                    {tab.label}
                    {workbenchTab === tab.id && (
                      <motion.div
                        layoutId="wb-tab-rule"
                        className="absolute bottom-0 left-0 right-0 h-[2px] bg-yellow-500"
                        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Workbench Content */}
              <div className="p-5 sm:p-6 md:p-7 min-h-[320px]">
                <AnimatePresence mode="wait">
                  {workbenchTab === 'findings' && (
                    <motion.div
                      key={`findings-${active.id}`}
                      variants={panelVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                      className="space-y-5"
                    >
                      {/* 01 Supports */}
                      <div className="pl-3.5 border-l-2 border-stone-300">
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-1">
                          01 / What this evidence proves
                        </p>
                        <p className="text-[13px] text-stone-800 leading-relaxed font-medium">
                          {active.supports}
                        </p>
                      </div>

                      {/* 02 Unproven (Warning boundary) */}
                      <div className="pl-3.5 border-l-2 border-yellow-500 bg-yellow-50/30 py-2 -my-1 rounded-r-xs">
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-800 mb-1 font-semibold">
                          02 / Control boundary &amp; what remains unproven
                        </p>
                        <p className="text-[13px] text-stone-700 leading-relaxed">
                          {active.unproven}
                        </p>
                      </div>

                      {/* 03 Next questions */}
                      <div className="pl-3.5 border-l-2 border-stone-900">
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-1">
                          03 / Substantive inquiry for operational team
                        </p>
                        <p className="text-[13px] text-stone-800 leading-relaxed italic font-serif">
                          "{active.nextQuestion}"
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {workbenchTab === 'artifact' && (
                    <motion.div
                      key={`artifact-${active.id}`}
                      variants={panelVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.25 }}
                    >
                      <EvidenceArtifactView sample={active.evidenceSample} />
                    </motion.div>
                  )}

                  {workbenchTab === 'gauge' && (
                    <motion.div
                      key={`gauge-${active.id}`}
                      variants={panelVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.25 }}
                      className="space-y-5 font-mono"
                    >
                      <div className="border-b border-stone-200 pb-3">
                        <span className="text-[9px] uppercase tracking-wider text-stone-500 block mb-1">
                          CONTROL ASSESSMENT MATRIX
                        </span>
                        <h4 className="text-sm font-semibold text-stone-900">
                          {`${active.label} / Evaluated Impact`}
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="bg-stone-50 p-3 border border-stone-200">
                          <span className="text-[9px] text-stone-500 uppercase block mb-1">Inherent Risk</span>
                          <span className="text-sm font-bold text-stone-900">{active.inherentRisk}</span>
                        </div>
                        <div className="bg-stone-50 p-3 border border-stone-200">
                          <span className="text-[9px] text-stone-500 uppercase block mb-1">Design Effectiveness</span>
                          <span className="text-sm font-bold text-stone-900">{active.designRating}</span>
                        </div>
                        <div className="bg-yellow-50 p-3 border border-yellow-300">
                          <span className="text-[9px] text-yellow-800 uppercase block mb-1 font-semibold">Operating Effectiveness</span>
                          <span className="text-xs font-bold text-stone-900">{active.operatingRating}</span>
                        </div>
                      </div>

                      <div className="p-3 bg-stone-50 border border-stone-200 text-[11px] text-stone-600 leading-relaxed">
                        <span className="font-bold text-stone-800">Assessor Note:</span> Operational verification requires continuous sampling beyond periodic policy certifications. Testing must sample both automated configurations and exception alert logs.
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

